import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { updatePassword } from '../Services/userapis';
import Loader from '../components/Loader';
import Hoc from '../components/HOC';
import { FaEye, FaEyeSlash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import LOCAL_KEYS from '../helpers/localstorage';
import { UserContext } from '../Context/User';

const UpdatePassword = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0); // 0-100
  const [strengthText, setStrengthText] = useState('');
  const [strengthColor, setStrengthColor] = useState('');
  const [strengthCriteria, setStrengthCriteria] = useState({
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
  });
  const navigate = useNavigate();
  const { updatePasswordExpiredState } = useContext(UserContext);

  useEffect(() => {
    // Prevent scroll on this page
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow || 'auto';
    };
  }, []);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    const criteria = {
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[^A-Za-z0-9]/.test(password),
      hasMinLength: password.length >= 8,
    };

    if (password.length > 0) {
      if (criteria.hasMinLength) strength += 20;
      if (criteria.hasLowercase) strength += 20;
      if (criteria.hasUppercase) strength += 20;
      if (criteria.hasNumber) strength += 20;
      if (criteria.hasSpecialChar) strength += 20;
    }

    let text = '';
    let color = '';

    if (password.length === 0) {
      text = '';
      color = '';
    } else if (strength <= 40) {
      text = 'Weak';
      color = 'bg-red-500';
    } else if (strength <= 80) {
      text = 'Medium';
      color = 'bg-yellow-500';
    } else {
      text = 'Strong';
      color = 'bg-green-500';
    }

    setPasswordStrength(strength);
    setStrengthText(text);
    setStrengthColor(color);
    setStrengthCriteria(criteria);
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    checkPasswordStrength(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await updatePassword({
        oldPassword,
        newPassword,
        confirmPassword,
      });
      toast.success('Password updated successfully. Please log in again.');
      
      // Clear password expired flag using context method
      updatePasswordExpiredState(false);
      localStorage.clear();
      
      navigate('/login');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      let errorMessage = 'Failed to update password. Please try again.';
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message === "Wrong current password") {
          errorMessage = "Old password doesn't match.";
        } else if (error.response.data.message === "New password cannot be the same as current password") {
          errorMessage = "New password cannot be the same as current password";
        } else if (error.response.data.message === "You cannot reuse any of your last 3 passwords") {
          errorMessage = "You cannot reuse any of your last 3 passwords";
        } else if (typeof error.response.data === 'object') {
          // If the error data is an object with field-specific errors
          const errorMessages = Object.values(error.response.data).join(', ');
          if (errorMessages) {
            errorMessage = errorMessages;
          }
        } else if (error.response.data.message) {
          // If there's a general message field
          errorMessage = error.response.data.message;
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full px-2 sm:px-4 py-6 sm:py-8">
      <Loader isLoading={isLoading} />
      <div className="bg-white p-4 sm:p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Update Password</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="oldPassword" className="block text-sm font-medium text-[#201F1E] mb-1">
              Old Password
            </label>
            <div className="relative">
              <input
                type={showOldPassword ? "text" : "password"}
                id="oldPassword"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#201F1E] pr-10"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
              />
              <span
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-500"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-[#201F1E] mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#201F1E] pr-10"
                value={newPassword}
                onChange={handleNewPasswordChange}
                required
              />
              <span
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-500"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            {newPassword.length > 0 && (
              <div className="mt-2 p-2 border border-gray-200 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-[#201F1E]">Password strength:</p>
                  <p className={`text-sm font-semibold ${strengthColor.replace('bg-', 'text-')}`}>
                    {strengthText}
                  </p>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                  <div
                    className={`h-2.5 rounded-full ${strengthColor}`}
                    style={{ width: `${passwordStrength}%` }}
                  ></div>
                </div>
                <div className="grid grid-cols-2 gap-y-1 text-sm">
                  <div className={`flex items-center ${strengthCriteria.hasLowercase ? 'text-green-600' : 'text-red-500'}`}>
                    {strengthCriteria.hasLowercase ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
                    Lowercase letter
                  </div>
                  <div className={`flex items-center ${strengthCriteria.hasUppercase ? 'text-green-600' : 'text-red-500'}`}>
                    {strengthCriteria.hasUppercase ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
                    Uppercase letter
                  </div>
                  <div className={`flex items-center ${strengthCriteria.hasNumber ? 'text-green-600' : 'text-red-500'}`}>
                    {strengthCriteria.hasNumber ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
                    Number
                  </div>
                  <div className={`flex items-center ${strengthCriteria.hasSpecialChar ? 'text-green-600' : 'text-red-500'}`}>
                    {strengthCriteria.hasSpecialChar ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
                    Special character
                  </div>
                  <div className={`flex items-center ${strengthCriteria.hasMinLength ? 'text-green-600' : 'text-red-500'}`}>
                    {strengthCriteria.hasMinLength ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
                    8+ characters
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#201F1E] mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#201F1E] pr-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <span
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-500"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-[#201F1E] hover:bg-[#201F1E] text-white font-semibold py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            Update Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default Hoc(UpdatePassword); 